import React, { useState } from "react";
import {
  useGetMyHeadersQuery,
  useToggleHeaderActiveMutation,
  useUpdateHeaderMutation,
} from "../store";
import { useGetMyItemsQuery, useToggleItemStockMutation } from "../store";
import {
  useGetMyModifierOptionsQuery,
  useToggleModifierOptionStockMutation,
} from "../store";

import { showToast, ToastType } from "../utils/toast.utils";
import { RiSearchLine } from "react-icons/ri";
import { Header, Item, ModifierOption } from "../data/interfaces";

interface SearchProps {
  searchTerm: string;
}

const HeaderList: React.FC<SearchProps> = ({ searchTerm }) => {
  const { data: headers, isLoading: isLoadingHeaders } = useGetMyHeadersQuery();
  const [toggleHeaderActive] = useToggleHeaderActiveMutation();
  const [updateHeader] = useUpdateHeaderMutation();

  const handleHeaderToggle = async (headerId: string, isActive: boolean) => {
    const result = await toggleHeaderActive({ headerId, isActive: !isActive });
    if (!("error" in result)) {
      showToast("Statut mis à jour", ToastType.SUCCESS);
    }
  };

  const handleHeaderOrderUpdate = async (
    headerId: string,
    newOrder: string
  ) => {
    const order = parseInt(newOrder, 10);
    if (isNaN(order) || order < 0) return;

    const result = await updateHeader({ _id: headerId, displayOrder: order });
    if (!("error" in result)) {
      showToast("Ordre mis à jour", ToastType.SUCCESS);
    }
  };

  const filteredHeaders = headers
    ?.filter((h) =>
      h.displayName.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => a.displayOrder - b.displayOrder);

  if (isLoadingHeaders)
    return (
      <div className="p-4 text-center text-gray-500">
        Chargement des catégories...
      </div>
    );

  return (
    <div className="flex flex-col gap-3 pb-10">
      {filteredHeaders?.map((header: Header) => (
        <div
          key={header._id}
          className="flex items-center justify-between p-4 bg-white border border-gray-100 rounded-xl shadow-sm hover:shadow-md transition-shadow"
        >
          <div className="flex items-center gap-4">
            <div className="flex flex-col">
              <span className="font-semibold text-gray-800 text-lg">
                {header.displayName}
              </span>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs text-gray-500 font-medium uppercase tracking-wide">
                  Ordre:
                </span>
                <input
                  type="number"
                  min="0"
                  className="w-16 border border-gray-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  defaultValue={header.displayOrder}
                  onBlur={(e) =>
                    handleHeaderOrderUpdate(header._id, e.target.value)
                  }
                />
              </div>
            </div>
          </div>
          <button
            onClick={() => handleHeaderToggle(header._id, header.isActive)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              header.isActive
                ? "bg-green-100 text-green-700 hover:bg-green-200"
                : "bg-red-100 text-red-700 hover:bg-red-200"
            }`}
          >
            {header.isActive ? "Actif" : "Inactif"}
          </button>
        </div>
      ))}
      {filteredHeaders?.length === 0 && (
        <div className="text-center py-10 text-gray-400 italic">
          Aucune catégorie trouvée
        </div>
      )}
    </div>
  );
};

const ItemList: React.FC<SearchProps> = ({ searchTerm }) => {
  const { data: items, isLoading: isLoadingItems } = useGetMyItemsQuery();
  const [toggleItemStock] = useToggleItemStockMutation();

  const handleItemStockToggle = async (itemId: string, haveStock: boolean) => {
    const result = await toggleItemStock({ itemId, haveStock: !haveStock });
    if (!("error" in result)) {
      showToast("Stock mis à jour", ToastType.SUCCESS);
    }
  };

  const filteredItems = items?.filter((i) =>
    i.displayName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoadingItems)
    return (
      <div className="p-4 text-center text-gray-500">
        Chargement des produits...
      </div>
    );

  return (
    <div className="flex flex-col gap-3 pb-10">
      {filteredItems?.map((item: Item) => (
        <div
          key={item._id}
          className="flex items-center justify-between p-4 bg-white border border-gray-100 rounded-xl shadow-sm hover:shadow-md transition-shadow"
        >
          <div>
            <span className="font-semibold text-gray-800 text-lg block">
              {item.displayName}
            </span>
            <span className="text-blue-600 font-medium">{item.price.text}</span>
          </div>
          <button
            onClick={() => handleItemStockToggle(item._id, item.haveStock)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              item.haveStock
                ? "bg-green-100 text-green-700 hover:bg-green-200"
                : "bg-red-100 text-red-700 hover:bg-red-200"
            }`}
          >
            {item.haveStock ? "En stock" : "Épuisé"}
          </button>
        </div>
      ))}
      {filteredItems?.length === 0 && (
        <div className="text-center py-10 text-gray-400 italic">
          Aucun produit trouvé
        </div>
      )}
    </div>
  );
};

const ModifierList: React.FC<SearchProps> = ({ searchTerm }) => {
  const { data: modifierOptions, isLoading: isLoadingModifiers } =
    useGetMyModifierOptionsQuery();
  const [toggleModifierStock] = useToggleModifierOptionStockMutation();

  const handleModifierStockToggle = async (
    modifierOptionId: string,
    haveStock: boolean
  ) => {
    const result = await toggleModifierStock({
      modifierOptionId,
      haveStock: !haveStock,
    });
    if (!("error" in result)) {
      showToast("Stock mis à jour", ToastType.SUCCESS);
    }
  };

  const filteredModifiers = modifierOptions?.filter((m) =>
    m.displayName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoadingModifiers)
    return (
      <div className="p-4 text-center text-gray-500">
        Chargement des suppléments...
      </div>
    );

  return (
    <div className="flex flex-col gap-3 pb-10">
      {filteredModifiers?.map((modifier: ModifierOption) => (
        <div
          key={modifier._id}
          className="flex items-center justify-between p-4 bg-white border border-gray-100 rounded-xl shadow-sm hover:shadow-md transition-shadow"
        >
          <div>
            <span className="font-semibold text-gray-800 text-lg block">
              {modifier.displayName}
            </span>
            <span className="text-blue-600 font-medium">
              {modifier.price.text}
            </span>
          </div>
          <button
            onClick={() =>
              handleModifierStockToggle(modifier._id, modifier.haveStock)
            }
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              modifier.haveStock
                ? "bg-green-100 text-green-700 hover:bg-green-200"
                : "bg-red-100 text-red-700 hover:bg-red-200"
            }`}
          >
            {modifier.haveStock ? "En stock" : "Épuisé"}
          </button>
        </div>
      ))}
      {filteredModifiers?.length === 0 && (
        <div className="text-center py-10 text-gray-400 italic">
          Aucun supplément trouvé
        </div>
      )}
    </div>
  );
};

// --- Main Menu Component ---
const Menu: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"Headers" | "Items" | "Modifiers">(
    "Headers"
  );
  const [searchTerm, setSearchTerm] = useState("");

  const tabs = [
    { id: "Headers", label: "Catégories" },
    { id: "Items", label: "Produits" },
    { id: "Modifiers", label: "Suppléments" },
  ];

  return (
    <div className="h-full flex flex-col bg-gray-50 overflow-hidden">
      <div className="p-6 bg-white border-b border-gray-100 shadow-sm z-10">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">Mon Menu</h1>

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex bg-gray-100 p-1 rounded-xl w-fit">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id as any);
                  setSearchTerm("");
                }}
                className={`px-6 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  activeTab === tab.id
                    ? "bg-white text-blue-600 shadow-sm"
                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-200/50"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="relative w-full md:w-auto">
            <RiSearchLine className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 w-full md:w-64 transition-all"
            />
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 scrollbar-hide">
        {activeTab === "Headers" && <HeaderList searchTerm={searchTerm} />}
        {activeTab === "Items" && <ItemList searchTerm={searchTerm} />}
        {activeTab === "Modifiers" && <ModifierList searchTerm={searchTerm} />}
      </div>
    </div>
  );
};

export default Menu;
